import type { BarreiraParaMarcar, Momento, MomentoDaProva, PrazoDaProva, ProvaOficial } from "../types"
import { nota as formatarNota, prazo } from "./format"

/*
 * As duas perguntas da última página do simulado, e o que a tela de resultado
 * faz com a resposta. Os valores são os de Usuario::MomentoDaProva; a copy
 * mora aqui para as duas telas dizerem a mesma coisa.
 *
 * O formato é o de questão de propósito — enunciado e alternativas com letra,
 * no mesmo cartão — para a pessoa responder no ritmo em que já está, sem
 * sentir que saiu do simulado e entrou numa pesquisa.
 */

export const PERGUNTA_MOMENTO = "Em que pé você está com a prova?"

export const OPCOES_MOMENTO: [MomentoDaProva, string][] = [
  ["pesquisando", "Ainda estou pesquisando sobre a habilitação."],
  ["vai_marcar", "Pretendo marcar a prova nos próximos meses."],
  ["marcou", "Já marquei a prova."],
]

export const PERGUNTA_PRAZO = "Para quando é a sua prova?"

export const OPCOES_PRAZO: [PrazoDaProva, string][] = [
  ["esta_semana", "Esta semana."],
  ["este_mes", "Ainda este mês."],
  ["proximos_meses", "Nos próximos três meses."],
  ["mais_adiante", "Mais adiante."],
]

export const PERGUNTA_BARREIRA = "O que falta para marcar?"

export const OPCOES_BARREIRA: [BarreiraParaMarcar, string][] = [
  ["decidir_habilitacao", "Decidir qual habilitação tirar."],
  ["fazer_curso", "Fazer o curso ou a parte prática."],
  ["nao_fui_atras", "Ainda não fui atrás de marcar."],
  ["dinheiro_ou_tempo", "Dinheiro ou tempo, por enquanto."],
]

/** "esta semana", "ainda este mês"… — o prazo como a pessoa o disse, para entrar numa frase. */
export const PRAZO_NA_FRASE: Record<PrazoDaProva, string> = {
  esta_semana: "esta semana",
  este_mes: "ainda este mês",
  proximos_meses: "nos próximos meses",
  mais_adiante: "mais adiante",
}

/** As respostas em andamento na última página: nada é obrigatório. */
export interface RespostaDeMomento {
  momento_da_prova: MomentoDaProva | null
  prazo_da_prova: PrazoDaProva | null
  barreira_para_marcar: BarreiraParaMarcar | null
}

export const SEM_RESPOSTA: RespostaDeMomento = {
  momento_da_prova: null,
  prazo_da_prova: null,
  barreira_para_marcar: null,
}

/** O que vai no POST /finalizar — só o detalhe que cabe no momento; nada se a primeira ficou em branco. */
export function corpoDoMomento(r: RespostaDeMomento): { momento: RespostaDeMomento } | undefined {
  if (r.momento_da_prova === null) return undefined
  const marcou = r.momento_da_prova === "marcou"
  return {
    momento: {
      momento_da_prova: r.momento_da_prova,
      prazo_da_prova: marcou ? r.prazo_da_prova : null,
      barreira_para_marcar: marcou ? null : r.barreira_para_marcar,
    },
  }
}

const marcou = (m: Momento | null): boolean => m?.momento_da_prova === "marcou"

/**
 * Prova marcada para esta semana ou este mês. A página fala em dias, não em
 * meses: foi o perfil de quem comprou (14/09/2026), e a leitura de 16/09 tinha
 * cinco com prova na semana, nota abaixo do corte e nenhuma compra.
 */
export function provaProxima(m: Momento | null): boolean {
  return marcou(m) && (m?.prazo_da_prova === "esta_semana" || m?.prazo_da_prova === "este_mes")
}

/** "Sua prova é esta semana" — ou só "já está marcada", se a pessoa não disse quando. */
function suaProva(m: Momento): string {
  return m.prazo_da_prova ? `Sua prova é ${PRAZO_NA_FRASE[m.prazo_da_prova]}` : "Sua prova já está marcada"
}

/**
 * A linha de apoio do diagnóstico reprovado. Para quem marcou, "ponto de
 * partida" soa como começo de uma jornada que não cabe no prazo: a nota vira
 * onde gastar o tempo que falta. Nulo = a frase padrão.
 */
export function subDoDiagnostico(m: Momento | null): string | null {
  if (!m || !marcou(m)) return null
  return `${suaProva(m)}. Este resultado mostra onde concentrar o tempo que falta.`
}

/**
 * A manchete da oferta, no tempo da pessoa. Quem marcou tem uma data e a
 * promessa é chegar nela pronto; quem não marcou (ou pulou) ouve a promessa
 * da landing, "marque sabendo que vai passar". Nenhuma frase inventa prazo:
 * o de quem marcou é o que ela mesma escolheu.
 */
export function mancheteDaOferta(m: Momento | null): string {
  if (m?.momento_da_prova === "marcou") {
    if (m.prazo_da_prova === "esta_semana") return "Sua prova é esta semana. Use os dias que faltam no que você errou."
    return `${suaProva(m)}. Chegue nela sabendo que vai passar.`
  }
  if (m?.momento_da_prova === "pesquisando") {
    return "Descubra o que a prova cobra de verdade antes de decidir — e marque sabendo que vai passar."
  }
  return "Treine até passar com margem antes de marcar sua prova."
}

/** O título da seção da oferta: "antes da prova" para quem marcou, "antes de marcar" para o resto. */
export function tituloDaOferta(m: Momento | null): string {
  if (provaProxima(m)) return "Estude o que você errou antes da sua prova"
  return marcou(m)
    ? "Treine até passar com margem antes da sua prova"
    : "Treine até passar com margem antes de marcar sua prova"
}

export function tituloDoCaminho(m: Momento | null): string {
  return marcou(m)
    ? "Existe um caminho daqui até o dia da sua prova"
    : "Existe um caminho daqui até estar pronto para a prova"
}

/**
 * O último benefício da oferta. Para quem marcou, seis meses "até depois da
 * prova" não dizem nada; o que diz é não perder o acesso se precisar remarcar.
 * Verdade pelo prazo contratado, sem prometer aprovação.
 */
export function beneficioDoPrazo(m: Momento | null, meses: number | null): [string, string] {
  if (marcou(m)) {
    return [
      meses ? `Acesso por ${prazo(meses)}` : "Acesso pelo período contratado",
      "Se precisar remarcar a prova, o acesso continua valendo. Sem contar simulado, sem crédito.",
    ]
  }
  return [
    meses ? `Treine quantas vezes quiser por ${prazo(meses)}` : "Treine quantas vezes quiser",
    "Até depois da sua prova. Sem contar simulado, sem crédito.",
  ]
}

/** A objeção de quem tem poucos dias, só para quem tem: aos outros, ela plantaria uma dúvida que não têm. */
export function objecaoDoPrazo(m: Momento | null): [string, string] | null {
  if (!provaProxima(m)) return null
  return [
    "“Minha prova é daqui a pouco. Ainda adianta?”",
    "Adianta se o tempo for para o lugar certo. O diagnóstico já mostrou em que unidades você perdeu ponto: estude o gabarito comentado delas primeiro e faça um simulado no formato da prova antes do dia. Não é garantia de aprovação — é o tempo que falta gasto onde ele rende mais.",
  ]
}

/** O fechamento da página. A promessa da landing ("marque sabendo…") contradiz quem já marcou. */
export function chamadaFinal(m: Momento | null): { titulo: [string, string]; texto: string } {
  if (marcou(m)) {
    return {
      titulo: ["Você já sabe onde perde ponto.", "Agora use o tempo até a prova nisso."],
      texto:
        "Estude seus erros, treine no formato da prova e acompanhe sua evolução até o dia. Chegue na prova sabendo que vai passar.",
    }
  }
  return {
    titulo: ["Você já descobriu seu ponto de partida.", "Agora falta fazer o caminho até a aprovação."],
    texto:
      "Estude seus erros, treine no formato da prova e acompanhe sua evolução até estar pronto. Aí marque a prova sabendo que vai passar.",
  }
}

/* ---------------------------------------------------------------------------
 * A carta para quem tem prova marcada (23/09/2026).
 *
 * Vinte e oito pessoas disseram "prova esta semana / este mês" entre 12 e
 * 23/09; duas compraram, e as outras viram a oferta uma vez e nunca voltaram.
 * A oferta vendia "preparação completa, seis meses" a quem tinha cinco dias.
 * O que muda aqui: a oferta passa a falar nos próximos dias, o botão diz o
 * que libera, o custo de reprovar entra na página (o e-mail que o contava
 * nunca chega a quem marcou para esta semana) e o número de candidatos entra
 * como prova social — contado no banco, não escrito.
 * ------------------------------------------------------------------------- */

/** "2 h", "1 h 30", "45 min" — como a Capitania escreve o tempo da prova. */
export function duracaoPorExtenso(minutos: number): string {
  const h = Math.floor(minutos / 60)
  const m = minutos % 60
  if (h === 0) return `${m} min`
  return m === 0 ? `${h} h` : `${h} h ${m}`
}

/** "40 questões em 2 h", ou só "o formato da prova" quando a habilitação não tem os números. */
export function formatoDaProva(prova: ProvaOficial): string {
  if (!prova.questoes) return "o formato da prova"
  const tempo = prova.duracao_minutos ? ` em ${duracaoPorExtenso(prova.duracao_minutos)}` : ""
  return `${prova.questoes} questões${tempo}`
}

/**
 * Para quem tem prova esta semana ou este mês: os próximos dias, não seis
 * meses. Agora, hoje, até a prova — cada linha sai de dado do payload. Nulo
 * para os outros, que continuam vendo a oferta de uma linha.
 */
export function planoAteAProva(
  m: Momento | null,
  r: { total: number; prova_oficial: ProvaOficial; prontidao: { seguidas: number; nota_minima: number } }
): [string, string][] | null {
  if (!provaProxima(m)) return null
  const vezes = POR_EXTENSO[r.prontidao.seguidas] ?? String(r.prontidao.seguidas)
  return [
    ["Agora", `o gabarito comentado destas ${r.total} questões, com a norma por trás de cada uma.`],
    ["Hoje ainda", `um simulado de ${formatoDaProva(r.prova_oficial)}, igual ao da Capitania.`],
    [
      "Até a prova",
      `repita até passar ${vezes} vezes seguidas com ${formatarNota(r.prontidao.nota_minima)} — aí você vai sabendo.`,
    ],
  ]
}

const POR_EXTENSO: Record<number, string> = { 1: "uma", 2: "duas", 3: "três" }

/** O botão diz o que o clique libera — "preparação" é justamente o que quem tem cinco dias não tem tempo de começar. */
export function rotuloDoBotao(m: Momento | null): string {
  if (provaProxima(m)) return "Liberar gabarito e simulados"
  return marcou(m) ? "Liberar minha preparação" : "Começar minha preparação"
}

/**
 * O custo de reprovar, só para quem marcou: para os outros ele mora no e-mail
 * D+4, que quem tem prova esta semana nunca recebe (o calendário curto acaba
 * no D+2). O fato é o da FAQ da landing: a GRU de quem reprova ou falta não é
 * reaproveitada.
 */
export function objecaoDoCusto(m: Momento | null): [string, string] | null {
  if (!marcou(m)) return null
  return [
    "“E se eu chegar torcendo?”",
    "Se reprovar ou faltar, a GRU que você pagou não é reaproveitada: é nova inscrição, nova taxa, nova ida à Capitania e a próxima data. Reprovar é quase sempre ter estudado a coisa errada, não pouco — e o diagnóstico acima já mostrou qual é a certa.",
  ]
}

/** "Mais de 180 candidatos já fizeram este diagnóstico grátis." — só com número que impressiona; abaixo de cem, silêncio. */
export function provaSocial(candidatos: number): string | null {
  if (candidatos < 100) return null
  const piso = Math.floor((candidatos - 1) / 10) * 10
  return `Mais de ${piso} candidatos já fizeram este diagnóstico grátis.`
}

/**
 * A linha de apoio de quem PASSOU no diagnóstico. "Passou" numa amostra de
 * dez virava "estou pronto": 32 pessoas com 6 ou mais saíram sem gerar um
 * Pix. A frase põe a amostra ao lado da prova real, em números.
 */
export function subDoAprovado(r: { total: number; prova_oficial: ProvaOficial }): string {
  const { questoes, acertos_para_aprovar: corte } = r.prova_oficial
  if (!questoes || !corte) {
    return "Esse resultado é seu ponto de partida — a prova real tem mais questões, tempo contado e a mesma nota de corte."
  }
  return `Esse resultado é seu ponto de partida — na prova real são ${formatoDaProva(r.prova_oficial)}, e ${corte} acertos aprovam. ${r.total} questões não dizem que você passaria; dizem por onde seguir.`
}

/** A manchete de quem passou no diagnóstico sem ter marcado: confirmar antes de marcar, não "treinar até passar". */
export function mancheteDoAprovado(m: Momento | null): string | null {
  if (marcou(m)) return null
  return "Passar numa amostra não é passar na prova. Confirme no formato real antes de marcar."
}
